import { sortByAsc, sortByDesc } from '@awork/_shared/functions/lodash'
import type { Task } from '@awork/features/task/models/task.model'
import type { SortingDirection, SortByOption } from '@awork/_shared/models/sort-by-option.model'

export function sortTasks(tasks: Task[], sortingOption: SortByOption): Task[] {
  if (!sortingOption) {
    return tasks
  }

  switch (sortingOption.value) {
    case 'workType':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.typeOfWork.name)
        : sortByDesc(tasks, t => t.typeOfWork.name)
    case 'startOn':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => new Date(t.startOn))
        : sortByDesc(tasks, t => new Date(t.startOn))
    case 'dueOn':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => new Date(t.dueOn))
        : sortByDesc(tasks, t => new Date(t.dueOn))
    case 'createdOn':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => new Date(t.createdOn))
        : sortByDesc(tasks, t => new Date(t.createdOn))
    case 'effort':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => (t.plannedDuration ? t.plannedDuration : 0))
        : sortByDesc(tasks, t => (t.plannedDuration ? t.plannedDuration : 0))
    case 'status':
    case 'taskStatus':
      return sortingOption.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.getOrderableTaskStatusName())
        : sortByDesc(tasks, t => t.getOrderableTaskStatusName())
    default:
      return tasks
  }
}

/**
 * Sort tasks by startOn or dueOn date.
 * @param {Task[]} tasks
 * @returns {Task[]}
 */
export function sortTasksByStartOnOrDueOn(tasks: Task[]): Task[] {
  return sortByAsc(tasks, t => (t.startOn ? new Date(t.startOn) : t.dueOn ? new Date(t.dueOn) : undefined))
}

/**
 * Compares project names without case differences. Tasks without a project or project name stay last in both directions.
 * @param {Task} taskA First task.
 * @param {Task} taskB Second task.
 * @param {SortingDirection} direction Sort direction.
 * @returns {number} Sort comparison.
 */
export function compareTasksByProject(taskA: Task, taskB: Task, direction: SortingDirection): number {
  const taskAProjectName = taskA.project?.name?.toLowerCase()
  const taskBProjectName = taskB.project?.name?.toLowerCase()

  if (!taskAProjectName && !taskBProjectName) {
    return 0
  }

  if (!taskAProjectName) {
    return 1
  }

  if (!taskBProjectName) {
    return -1
  }

  const comparison = taskAProjectName.localeCompare(taskBProjectName)

  return direction === 'asc' ? comparison : -comparison
}
